'use client';

import { useState } from 'react';
import type { CreatorExperienceId } from './experience-config';
import {
	Globe,
	FileText,
	Link2,
	Contact,
	IdCard,
	MessageCircle,
	UtensilsCrossed,
	Smartphone,
	TicketPercent,
	Wifi,
	CalendarDays,
	CheckCircle2,
	Sparkles,
	X,
	ChevronRight,
} from 'lucide-react';

export interface ExperienceItem {
	id: CreatorExperienceId;
	title: string;
	description: string;
	available: boolean;
	icon: React.ComponentType<{ className?: string }>;
	colorStyle: {
		bg: string;
		text: string;
		border: string;
		ring: string;
	};
}

export const EXPERIENCES: ExperienceItem[] = [
	{
		id: 'website',
		title: 'Website',
		description: 'Link to any website or web page URL',
		available: true,
		icon: Globe,
		colorStyle: {
			bg: 'bg-sky-50',
			text: 'text-sky-600',
			border: 'border-sky-200/80',
			ring: 'ring-sky-500/10',
		},
	},
	{
		id: 'pdf',
		title: 'PDF',
		description: 'Show a PDF document, brochure or menu',
		available: true,
		icon: FileText,
		colorStyle: {
			bg: 'bg-rose-50',
			text: 'text-rose-600',
			border: 'border-rose-200/80',
			ring: 'ring-rose-500/10',
		},
	},
	{
		id: 'social-links',
		title: 'List of Links',
		description: 'Share multiple social profiles & links',
		available: true,
		icon: Link2,
		colorStyle: {
			bg: 'bg-blue-50',
			text: 'text-blue-600',
			border: 'border-blue-200/80',
			ring: 'ring-blue-500/10',
		},
	},
	{
		id: 'vcard',
		title: 'vCard',
		description: 'Save contact card to phone address book',
		available: true,
		icon: Contact,
		colorStyle: {
			bg: 'bg-indigo-50',
			text: 'text-indigo-600',
			border: 'border-indigo-200/80',
			ring: 'ring-indigo-500/10',
		},
	},
	{
		id: 'business-card',
		title: 'Business Card',
		description: 'Digital card with 2-way contact exchange',
		available: true,
		icon: IdCard,
		colorStyle: {
			bg: 'bg-teal-50',
			text: 'text-[#0f766e]',
			border: 'border-teal-200/80',
			ring: 'ring-teal-500/10',
		},
	},
	{
		id: 'whatsapp',
		title: 'WhatsApp',
		description: 'Get direct WhatsApp chat messages',
		available: true,
		icon: MessageCircle,
		colorStyle: {
			bg: 'bg-emerald-50',
			text: 'text-emerald-600',
			border: 'border-emerald-200/80',
			ring: 'ring-emerald-500/10',
		},
	},
	{
		id: 'menu',
		title: 'Menu',
		description: 'Create a restaurant or cafe food menu',
		available: true,
		icon: UtensilsCrossed,
		colorStyle: {
			bg: 'bg-amber-50',
			text: 'text-amber-600',
			border: 'border-amber-200/80',
			ring: 'ring-amber-500/10',
		},
	},
	{
		id: 'app',
		title: 'Apps',
		description: 'Redirect to App Store & Google Play',
		available: true,
		icon: Smartphone,
		colorStyle: {
			bg: 'bg-violet-50',
			text: 'text-violet-600',
			border: 'border-violet-200/80',
			ring: 'ring-violet-500/10',
		},
	},
	{
		id: 'coupon',
		title: 'Coupon',
		description: 'Share discounts & promo vouchers',
		available: true,
		icon: TicketPercent,
		colorStyle: {
			bg: 'bg-orange-50',
			text: 'text-orange-600',
			border: 'border-orange-200/80',
			ring: 'ring-orange-500/10',
		},
	},
	{
		id: 'wifi',
		title: 'WiFi',
		description: 'Connect to a Wi-Fi network instantly',
		available: true,
		icon: Wifi,
		colorStyle: {
			bg: 'bg-cyan-50',
			text: 'text-cyan-600',
			border: 'border-cyan-200/80',
			ring: 'ring-cyan-500/10',
		},
	},
	{
		id: 'event',
		title: 'Event',
		description: 'Share event details & calendar invite',
		available: true,
		icon: CalendarDays,
		colorStyle: {
			bg: 'bg-purple-50',
			text: 'text-purple-600',
			border: 'border-purple-200/80',
			ring: 'ring-purple-500/10',
		},
	},
];

interface MenuChoiceModalProps {
	isOpen: boolean;
	onClose: () => void;
	onSelect: (experience: CreatorExperienceId) => void;
	onHover?: (experience: CreatorExperienceId | null) => void;
}

function MenuChoiceModal({ isOpen, onClose, onSelect, onHover }: MenuChoiceModalProps) {
	if (!isOpen) return null;

	const handleClose = () => {
		onHover?.(null);
		onClose();
	};

	return (
		<div
			className="fixed inset-0 z-50 flex items-center justify-center bg-slate-950/50 backdrop-blur-sm p-4 animate-in fade-in duration-200"
			onClick={handleClose}
		>
			<div
				className="relative w-full max-w-lg rounded-2xl bg-white p-6 sm:p-7 shadow-2xl border border-slate-100 animate-in zoom-in-95 duration-200 select-none"
				onClick={(e) => e.stopPropagation()}
				role="dialog"
				aria-modal="true"
				aria-labelledby="menu-modal-title"
			>
				{/* Close button */}
				<button
					type="button"
					onClick={handleClose}
					className="absolute top-4 right-4 size-8 rounded-lg flex items-center justify-center text-slate-400 hover:text-slate-900 hover:bg-slate-100 transition cursor-pointer"
					aria-label="Close"
				>
					<X className="size-4" />
				</button>

				{/* Header */}
				<div className="text-left mb-6 pr-8">
					<span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-semibold bg-amber-50 text-amber-800 border border-amber-200/80 mb-2">
						<UtensilsCrossed className="size-3.5 text-amber-600" />
						Menu QR Code
					</span>
					<h2
						id="menu-modal-title"
						className="text-xl sm:text-2xl font-bold tracking-tight text-slate-950"
					>
						Choose your menu format
					</h2>
					<p className="mt-1 text-sm text-slate-600">
						Select how you want your customers to view and interact with your menu.
					</p>
				</div>

				{/* 3 Options */}
				<div className="space-y-3">
					{/* Option 1: Digital Interactive Menu */}
					<button
						type="button"
						onMouseEnter={() => onHover?.('menu')}
						onClick={() => {
							onClose();
							onSelect('menu');
						}}
						className="w-full flex items-center justify-between p-4 rounded-xl border border-slate-200 bg-white hover:border-[#0f766e] hover:bg-emerald-50/15 hover:shadow-xs transition-all group text-left cursor-pointer focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#0f766e]"
					>
						<div className="flex items-start gap-3.5 min-w-0 pr-2">
							<span className="grid size-11 place-items-center rounded-xl bg-amber-50 text-amber-600 border border-amber-200 shadow-xs shrink-0 group-hover:scale-105 transition-transform">
								<UtensilsCrossed className="size-5" />
							</span>
							<div className="min-w-0">
								<div className="flex items-center gap-2">
									<span className="text-sm font-semibold text-slate-900 leading-tight">
										Digital interactive menu
									</span>
									<span className="rounded-full bg-emerald-50 border border-emerald-200 px-2 py-0.5 text-[10px] font-bold text-emerald-800 uppercase tracking-wider">
										Recommended
									</span>
								</div>
								<p className="text-xs text-slate-500 mt-1 leading-normal">
									Sections, dietary tags, images, pricing, and live mobile preview.
								</p>
							</div>
						</div>
						<ChevronRight className="size-5 text-slate-400 group-hover:text-slate-900 group-hover:translate-x-0.5 transition shrink-0" />
					</button>

					{/* Option 2: PDF Menu */}
					<button
						type="button"
						onMouseEnter={() => onHover?.('pdf')}
						onClick={() => {
							onClose();
							onSelect('pdf');
						}}
						className="w-full flex items-center justify-between p-4 rounded-xl border border-slate-200 bg-white hover:border-slate-900 hover:bg-slate-50/70 hover:shadow-xs transition-all group text-left cursor-pointer focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-slate-900"
					>
						<div className="flex items-start gap-3.5 min-w-0 pr-2">
							<span className="grid size-11 place-items-center rounded-xl bg-rose-50 text-rose-600 border border-rose-200/80 shadow-xs shrink-0 group-hover:scale-105 transition-transform">
								<FileText className="size-5" />
							</span>
							<div className="min-w-0">
								<span className="text-sm font-semibold text-slate-900 leading-tight">
									Upload a PDF menu
								</span>
								<p className="text-xs text-slate-500 mt-1 leading-normal">
									Upload an existing food or drink brochure, wine list, or paper menu PDF.
								</p>
							</div>
						</div>
						<ChevronRight className="size-5 text-slate-400 group-hover:text-slate-900 group-hover:translate-x-0.5 transition shrink-0" />
					</button>

					{/* Option 3: Link to Menu */}
					<button
						type="button"
						onMouseEnter={() => onHover?.('website')}
						onClick={() => {
							onClose();
							onSelect('website');
						}}
						className="w-full flex items-center justify-between p-4 rounded-xl border border-slate-200 bg-white hover:border-slate-900 hover:bg-slate-50/70 hover:shadow-xs transition-all group text-left cursor-pointer focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-slate-900"
					>
						<div className="flex items-start gap-3.5 min-w-0 pr-2">
							<span className="grid size-11 place-items-center rounded-xl bg-sky-50 text-sky-600 border border-sky-200/80 shadow-xs shrink-0 group-hover:scale-105 transition-transform">
								<Globe className="size-5" />
							</span>
							<div className="min-w-0">
								<span className="text-sm font-semibold text-slate-900 leading-tight">
									Link to an online menu
								</span>
								<p className="text-xs text-slate-500 mt-1 leading-normal">
									Redirect visitors directly to an existing webpage, ordering platform, or URL.
								</p>
							</div>
						</div>
						<ChevronRight className="size-5 text-slate-400 group-hover:text-slate-900 group-hover:translate-x-0.5 transition shrink-0" />
					</button>
				</div>
			</div>
		</div>
	);
}

export function ExperiencePicker({
	value,
	onChange,
	onSelect,
	onHover,
}: {
	value?: CreatorExperienceId | null;
	onChange?: (value: CreatorExperienceId) => void;
	onSelect: (value: CreatorExperienceId) => void;
	onHover?: (value: CreatorExperienceId | null) => void;
}) {
	const [isMenuChoiceOpen, setIsMenuChoiceOpen] = useState(false);

	const handleCardClick = (id: CreatorExperienceId) => {
		if (id === 'menu') {
			setIsMenuChoiceOpen(true);
		} else {
			onSelect(id);
			onChange?.(id);
		}
	};

	const currentSelectedId = value || 'website';

	return (
		<div>
			{/* Header */}
			<div className="mb-6 sm:mb-8">
				<div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-emerald-50 border border-emerald-200/80 text-emerald-800 text-xs font-semibold mb-2.5">
					<span className="size-1.5 rounded-full bg-emerald-500 animate-pulse" />
					<span>Step 1 of 4</span>
					<span className="text-emerald-300">•</span>
					<span>100% Free</span>
				</div>
				<h1 className="text-2xl font-extrabold tracking-tight text-slate-950 sm:text-3xl">
					1. Select a type of QR code
				</h1>
				<p className="mt-1.5 text-sm text-slate-600">
					Choose what you want people to see when they scan your code. Hover over any type to
					preview on the right.
				</p>
			</div>

			{/* 4-column Colorful Card Grid */}
			<div
				className="grid grid-cols-2 sm:grid-cols-3 xl:grid-cols-4 gap-3 sm:gap-4"
				onMouseLeave={() => onHover?.(null)}
			>
				{EXPERIENCES.map((exp) => {
					const Icon = exp.icon;
					const selected = currentSelectedId === exp.id;
					return (
						<button
							key={exp.id}
							type="button"
							disabled={!exp.available}
							aria-disabled={!exp.available}
							aria-pressed={selected}
							onMouseEnter={() => exp.available && onHover?.(exp.id)}
							onFocus={() => exp.available && onHover?.(exp.id)}
							onClick={() => exp.available && handleCardClick(exp.id)}
							className={`group relative rounded-2xl border p-4 sm:p-5 flex flex-col items-center text-center justify-between transition-all duration-200 min-h-[145px] sm:min-h-[160px] cursor-pointer select-none ${
								selected
									? 'border-2 border-[#0f766e] ring-4 ring-[#0f766e]/15 shadow-md bg-emerald-50/15'
									: 'border-slate-200/90 bg-white hover:border-[#0f766e]/60 hover:shadow-md hover:-translate-y-0.5'
							}`}
						>
							{/* Selected Checkmark Badge */}
							{selected && (
								<div className="absolute top-2.5 right-2.5 animate-in zoom-in-75 duration-150">
									<CheckCircle2 className="size-4 sm:size-4.5 text-[#0f766e]" />
								</div>
							)}

							{/* Colorful Icon Badge */}
							<div
								className={`size-12 sm:size-14 rounded-2xl flex items-center justify-center transition-transform duration-200 group-hover:scale-105 shadow-2xs border ${exp.colorStyle.bg} ${exp.colorStyle.text} ${exp.colorStyle.border} ${exp.colorStyle.ring}`}
							>
								<Icon className="size-6 sm:size-7" />
							</div>

							{/* Title and Description */}
							<div className="mt-2.5 w-full">
								<span className="block text-sm sm:text-base font-bold text-slate-900 leading-tight">
									{exp.title}
								</span>
								<span className="mt-1 block text-xs text-slate-500 line-clamp-2 leading-relaxed">
									{exp.description}
								</span>
							</div>
						</button>
					);
				})}
			</div>

			{/* Bottom Action Footer */}
			<div className="mt-8 pt-6 border-t border-slate-100 flex flex-col sm:flex-row items-center justify-between gap-3 text-xs text-slate-500">
				<div className="flex items-center gap-1.5 font-medium">
					<Sparkles className="size-3.5 text-emerald-600" />
					<span>100% Free • Unlimited Scans • Dynamic Editing</span>
				</div>
				<div className="text-slate-400 font-medium flex items-center gap-1.5">
					<span className="size-1.5 rounded-full bg-emerald-500 animate-pulse" />
					<span>Hover to preview • Click any type to start immediately</span>
				</div>
			</div>

			{/* Modal for Menu Options */}
			<MenuChoiceModal
				isOpen={isMenuChoiceOpen}
				onClose={() => {
					setIsMenuChoiceOpen(false);
					onHover?.(null);
				}}
				onSelect={(id) => {
					setIsMenuChoiceOpen(false);
					onSelect(id);
					onChange?.(id);
				}}
				onHover={onHover}
			/>
		</div>
	);
}
